"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, ArrowRight, X } from "lucide-react";

interface TopAnnouncementBarProps {
  text: string;
  linkText?: string;
  linkUrl?: string;
  color?: string;
}

// Zmienna CSS odczytywana przez fixed'owany <Navbar>, żeby nie nachodził
// na ten pasek — patrz src/components/sections/Navbar.tsx.
const HEIGHT_VAR = "--ns-announcement-h";

/**
 * Przyklejony do góry pasek ogłoszeń, widoczny na stronach publicznych
 * serwisu (nie na platformie/panelu ani w adminie). Treść, link i kolor
 * konfigurowane z panelu admina (Ustawienia). Zamknięcie zapamiętywane jest
 * w localStorage — klucz zależy od treści, więc nowy komunikat znów pokaże
 * się odwiedzającym, którzy zamknęli poprzedni.
 */
export default function TopAnnouncementBar({
  text,
  linkText = "",
  linkUrl = "",
  color = "#1d4ed8",
}: TopAnnouncementBarProps) {
  const pathname = usePathname() || "/";
  const routeAllowed = !pathname.startsWith("/platforma") && !pathname.startsWith("/admin");
  const storageKey = `ns_top_banner_dismissed_${text.slice(0, 40)}_${linkUrl.slice(0, 40)}`;
  const [visible, setVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!routeAllowed) {
      setVisible(false);
      return;
    }
    try {
      if (localStorage.getItem(storageKey) !== "1") setVisible(true);
    } catch {
      setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, routeAllowed]);

  // Mierzymy realną wysokość paska (zmienia się przez zawijanie tekstu na
  // wąskich ekranach / animację wejścia) i udostępniamy ją jako zmienną CSS,
  // żeby fixed'owany nagłówek mógł zejść poniżej zamiast go zasłaniać.
  useEffect(() => {
    const el = barRef.current;
    if (!visible || !el) {
      document.documentElement.style.setProperty(HEIGHT_VAR, "0px");
      return;
    }
    const update = () => {
      document.documentElement.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty(HEIGHT_VAR, "0px");
    };
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      /* ignore */
    }
  };

  if (!text) return null;

  const isInternalLink = linkUrl.startsWith("/");

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="sticky top-0 z-[60] overflow-hidden text-white"
          style={{ backgroundColor: color }}
        >
          <div ref={barRef} className="mx-auto flex max-w-7xl items-center gap-2.5 px-3 py-2 sm:gap-3 sm:px-4">
            <Megaphone className="hidden h-4 w-4 shrink-0 sm:block" />
            <p className="min-w-0 flex-1 text-[13px] font-medium leading-snug sm:text-sm">
              {text}
            </p>
            {linkText && linkUrl && (
              isInternalLink ? (
                <Link
                  href={linkUrl}
                  className="shrink-0 inline-flex items-center gap-1 text-[13px] font-bold underline underline-offset-2 hover:no-underline sm:text-sm"
                >
                  {linkText} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ) : (
                <a
                  href={linkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 inline-flex items-center gap-1 text-[13px] font-bold underline underline-offset-2 hover:no-underline sm:text-sm"
                >
                  {linkText} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              )
            )}
            <button
              type="button"
              onClick={dismiss}
              aria-label="Zamknij"
              className="shrink-0 rounded-full p-1 text-white/80 transition-colors hover:bg-white/15 hover:text-white cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
